import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    InputAdornment,
    Chip,
    TablePagination,
} from "@mui/material";
import FilterButton from "../FilterDropdownButton";
import { CiSearch } from "react-icons/ci";
import { GoDotFill } from "react-icons/go";
import DateRangePickerButton from "./DateRangePickerDropdown";
import { useNavigate } from "react-router-dom";
import { fetchOrders, selectOrders } from "../../../redux/slices/myAccount/orderTableSlice";
import { useDispatch, useSelector } from "react-redux";
import { useLanguage } from "../../../context/LanguageContext/LanguageContext";
import CustomPagination from "../Pagination/Pagination";
import Loading from "../../UI/Loading/Loading";

const OrderTable = () => {
    const { translations, language, direction } = useLanguage();
    const t = translations;
    const navigate = useNavigate(); // Initialize useNavigate
    const { isLoggedIn, user, token } = useSelector(state => state.login);
    const dispatch = useDispatch();
    const orders = useSelector(state => state.orders.orders);
    const { loading, error } = useSelector((state) => state.orders);

    const handleDateRangeChange = ({ startDate, endDate }) => {
        setStartDate(startDate);
        setEndDate(endDate);
    };
    // Local States
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedStatus, setSelectedStatus] = useState(null); // Order status
    const [startDate, setStartDate] = useState(null); 
    const [endDate, setEndDate] = useState(null);
    const [year, setYear] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [selectedPeriod, setSelectedPeriod] = useState("All Time");
    const [selectedFilter, setSelectedFilter] = useState("All");

    useEffect(() => {
        // Determine the validStatus based on selectedFilter
        let validStatus;
        if (selectedFilter === "All") {
            validStatus = null; // All statuses (null)
        } else if (selectedFilter === "Pending") {
            validStatus = 4; // Status 1 for new and pending
        } else if (selectedFilter === "rejected") {
            validStatus = 2; // Status 2 for rejected
        } else if (selectedFilter === "Accepted") {
            validStatus = 3; // Status 3 for accepted
        }

        // Prepare the search term for the fetch request
        const search = searchTerm
            ? { value: searchTerm, regex: false }
            : null;

        const payload = {
            draw: page + 1,
            start: page * rowsPerPage,
            length: rowsPerPage,
            search: search || null,
            text: searchTerm || null,
            startDate: startDate ? startDate.toISOString() : null,
            endDate: endDate ? endDate.toISOString() : null,
            orderStatus: validStatus || null,
            customerFk: user ? user.id : null,
            year: startDate ? new Date(startDate).getFullYear() : null, // Optional: derive year from startDate
        }; 
        // Dispatch the action to fetch orders
        dispatch(fetchOrders({ payload, token, language }))
            .then(response => {
                // console.log("response", response); // Log the response from the thunk
            })
            .catch(error => {
                // console.error("Error fetching orders: ", error);
            });
    }, [dispatch, searchTerm, startDate, endDate, selectedFilter, page, rowsPerPage, token, language, user]);
    const filteredOrders = orders.filter(order => {
        const matchesSearchTerm = searchTerm
            ? order.Description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            order.OrderNumber?.toString().toLowerCase().includes(searchTerm.toLowerCase())
            : true;
        return matchesSearchTerm;
    });

    const totalOrders = filteredOrders.length;
    const totalPages = Math.ceil(totalOrders / rowsPerPage);
    const [currentPage, setCurrentPage] = useState(0);
    const itemsPerPage = 5;
    const startIndex = Math.min(currentPage * itemsPerPage, filteredOrders.length);
    const endIndex = Math.min(startIndex + itemsPerPage, filteredOrders.length);
    const paginatedOrders = filteredOrders.slice(startIndex, endIndex);
    const handleSearchChange = (event) => setSearchTerm(event.target.value);
    const navigateToOrderDetails = orderId => {
        const clickedOrder = orders.find(order => order.OrderId === orderId);
        navigate(`/my-account/orders/${orderId}`, { state: { OrderId: orderId } });
    };
    const handleFilterChange = (filter) => setSelectedFilter(filter);
    if (loading) return <Loading />;
    if (!isLoggedIn) {
        navigate('/signin');
        return null;
    }
    return (
        <Box p={3}>
            <Box display="flex" justifyContent="space-between" mb={2} sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 2,
                flexDirection: { lg: "row", md: "column", xs: "column" },
                mt: { md: "0px", xs: "30px" },
                gap: "20px"
            }}>
                <TextField
                    variant="outlined"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    placeholder={t.orderTable_searchPlaceholder}
                    InputProps={{
                        disableUnderline: true,
                        endAdornment: (
                            <InputAdornment position="end">
                                <CiSearch style={{ fontSize: '28px', color: '#A4A4A4' }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        width: '300px',
                        height: '56px',
                        backgroundColor: '#FDFDFD',
                        boxShadow: '0px 11px 23px 0px #00000005',
                        borderRadius: '12px',
                        '& .MuiOutlinedInput-root': { border: 'none' },
                        '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
                        '& .MuiInputBase-input::placeholder': { fontSize: '16px' },
                    }}
                />
                <Box sx={{ display: "flex", gap: "24px", }}>
                    <DateRangePickerButton t={t} direction={direction} selectedPeriod={t.orderTable_filters_selectDateRange} onPeriodChange={handleDateRangeChange} />

                    <FilterButton
                        selectedFilter={selectedFilter}
                        onFilterChange={handleFilterChange}
                        labels={{
                            acceptedLabel: t.orderTable_filterLabels_rejectedLabel,
                            pendingLabel: t.orderTable_filterLabels_pendingLabel,
                            allLabel: t.orderTable_filterLabels_allLabel,
                        }}
                    />

                </Box>

            </Box>

            <TableContainer component={Paper} sx={{
                maxWidth: {
                    xs: "auto",        // Default for extra small screens
                    sm: "auto",        // Default for small screens
                    md: "600px",       // For medium screens (900px and above)
                    lg: "100%",        // For large screens
                },
                overflowX: "auto",  // Enable horizontal scrolling
                "@media (min-width: 900px) and (max-width: 1004px)": { // Custom range for 900px to 1004px screens
                    maxWidth: "490px", // Set maxWidth to 590px for this range
                },
            }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell><strong>{t.orderTable_orderNumber}</strong></TableCell>
                            <TableCell><strong>{t.date}</strong></TableCell>
                            <TableCell><strong>{t.description2}</strong></TableCell>
                            <TableCell><strong>{t.orderTable_notificationType}</strong></TableCell>
                            <TableCell><strong>{t.unitDetails.priority}</strong></TableCell>

                            <TableCell><strong>{t.status}</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredOrders.length > 0 ? (
                            paginatedOrders.map(order => (
                                <TableRow
                                    key={order.OrderId}
                                    hover
                                    onClick={() => navigateToOrderDetails(order.OrderId)}
                                    style={{ cursor: "pointer" }}
                                >
                                    <TableCell sx={{ wordBreak: 'break-word' }}>
                                        <Button
                                            variant="text"
                                            style={{ color: "#0056D2" }}
                                            onClick={(e) => {
                                                e.stopPropagation(); // Prevent row click
                                                navigateToOrderDetails(order.OrderId);
                                            }}
                                        >
                                            {order.OrderNumber || "null"}
                                        </Button>
                                    </TableCell >
                                    <TableCell sx={{ wordBreak: 'break-word' }}>{order.RequestDate || "null"}</TableCell>
                                    <TableCell sx={{ wordBreak: 'break-word' }}>{order.Description || "null"}</TableCell>
                                    <TableCell sx={{ wordBreak: 'break-word' }}>{order.NotificationType.TypeNameLocalized || "null"}</TableCell>
                                    <TableCell sx={{ wordBreak: 'break-word' }}>{order.PeriorityName || "null"}</TableCell>

                                    <TableCell>
                                        <Chip
                                            label={
                                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                                    <GoDotFill
                                                        style={{
                                                            fontSize: "10px",
                                                            color:
                                                                order.StatusName === 'Closed'
                                                                    ? "#209F84"
                                                                    : order.status === t.newUnit
                                                                        ? "#FF5C00"
                                                                        : "#FF2C53",
                                                            marginRight: "8px",
                                                        }}
                                                    />
                                                    {order.StatusName}
                                                </Box>
                                            }
                                            sx={{
                                                backgroundColor:
                                                    order.StatusName === 'Closed'
                                                        ? "#209F841A"
                                                        : order.StatusName === t.newUnit
                                                            ? "#FF5C0026"
                                                            : "#F3E9E8",
                                                color:
                                                    order.StatusName === 'Closed'
                                                        ? "#209F84"
                                                        : order.StatusName === t.newUnit
                                                            ? "#FF5C00"
                                                            : "#FF2C53",
                                                fontWeight: "bold",
                                                borderRadius: "8px",
                                                padding: "8px 12px",
                                            }}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={6} align="center" style={{ color: "#A4A4A4", fontWeight: "bold" }}>
                                    {t.noData}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <CustomPagination
                page={currentPage}
                setPage={setCurrentPage}
                totalData={totalOrders}
                itemsPerPage={itemsPerPage}
            />


        </Box>
    );
};

export default OrderTable;
